import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { SyncActivity } from '@/lib/types'
import { useApiRoutes } from '@/lib/utils/useApiRoutes'
import { onApiMutation } from '@/lib/utils/apiMutations'

/** Poll cadence while a sync is in flight: locker acks land within seconds. */
export const SYNC_ACTIVITY_ACTIVE_POLL_MS = 3_000
/** Poll cadence when everything is in sync (picks up other admins' changes). */
export const SYNC_ACTIVITY_IDLE_POLL_MS = 60_000
/** A save's sync is recorded after the response; config pushes after a short debounce. */
export const SYNC_ACTIVITY_AFTER_SAVE_MS = 1_500
/** How long after a save the pane keeps the fast cadence while its sync appears. */
export const SYNC_ACTIVITY_SAVE_WATCH_MS = 10_000

/**
 * The operating tenant's sync feed as the server returns it (latest entries,
 * newest first). `current` is the newest one still in progress;
 * `recentlySettled` is the newest one a read saw finish, including one that
 * appeared already finished (a fast sync can settle between two reads), so the
 * pane can show its result. The first read is the baseline: history is not
 * news. Polls fast while anything is in flight or a save just happened, slowly
 * otherwise, and skips the read while the tab is hidden.
 */
export const useSyncActivityStore = defineStore('syncActivity', () => {
  const routes = useApiRoutes()
  const items = ref<SyncActivity[]>([])
  const recentlySettled = ref<SyncActivity | null>(null)
  let loaded = false

  const active = computed(() => items.value.filter((a) => a.status === 'in_progress'))
  const current = computed<SyncActivity | null>(() => active.value[0] ?? null)

  async function refresh(): Promise<void> {
    let next: SyncActivity[]
    try {
      next = (await routes.SYNC_ACTIVITIES_RECENT.get<SyncActivity[]>().notifyOnError(false).send()) ?? []
    } catch {
      // Quiet: the pane keeps the last known feed through a network blip.
      return
    }
    if (loaded) {
      const before = new Map(items.value.map((a) => [a.id, a.status]))
      const newestKnown = items.value.reduce((max, a) => Math.max(max, a.id), 0)
      const settled = next.find(
        (a) =>
          a.status !== 'in_progress' &&
          (before.get(a.id) === 'in_progress' || (!before.has(a.id) && a.id > newestKnown)),
      )
      if (settled) recentlySettled.value = settled
    }
    items.value = next
    loaded = true
  }

  let running = false
  let timer: ReturnType<typeof setTimeout> | null = null
  let watchUntil = 0
  let stopWatchingSaves: (() => void) | null = null

  function schedule(ms: number): void {
    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(() => void tick(), ms)
  }

  async function tick(): Promise<void> {
    timer = null
    if (document.visibilityState === 'visible') await refresh()
    // A save during the read has already scheduled the next one.
    if (!running || timer !== null) return
    const fast = active.value.length > 0 || Date.now() < watchUntil
    schedule(fast ? SYNC_ACTIVITY_ACTIVE_POLL_MS : SYNC_ACTIVITY_IDLE_POLL_MS)
  }

  function onVisibilityChange(): void {
    if (running && document.visibilityState === 'visible') schedule(0)
  }

  function start(): void {
    if (running) return
    running = true
    document.addEventListener('visibilitychange', onVisibilityChange)
    stopWatchingSaves = onApiMutation(() => {
      watchUntil = Date.now() + SYNC_ACTIVITY_SAVE_WATCH_MS
      schedule(SYNC_ACTIVITY_AFTER_SAVE_MS)
    })
    schedule(0)
  }

  function stop(): void {
    running = false
    if (timer !== null) clearTimeout(timer)
    timer = null
    document.removeEventListener('visibilitychange', onVisibilityChange)
    stopWatchingSaves?.()
    stopWatchingSaves = null
    items.value = []
    recentlySettled.value = null
    loaded = false
  }

  return { items, active, current, recentlySettled, refresh, start, stop }
})
